'use client'

import { useEffect, useState } from 'react'
import '@/app/public.css'
import './landing.css'

/* Intersection Observer for fade-up animations */
function FadeUpAnimations() {
  useEffect(() => {
    const fadeEls = document.querySelectorAll('.fade-up')
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((el) => {
          if (el.isIntersecting) {
            el.target.classList.add('visible')
            observer.unobserve(el.target)
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    )

    fadeEls.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return null
}

/* Theme Toggle Component */
function ThemeToggle() {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark')

  useEffect(() => {
    const savedTheme = localStorage.getItem('landing-theme') as 'light' | 'dark' | null
    const initialTheme = savedTheme || 'dark'
    setTheme(initialTheme)
    document.documentElement.classList.toggle('light', initialTheme === 'light')
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
    localStorage.setItem('landing-theme', newTheme)
    document.documentElement.classList.toggle('light', newTheme === 'light')
  }

  return (
    <button
      onClick={toggleTheme}
      aria-label="Toggle theme"
      style={{
        background: 'none',
        border: '1px solid var(--rule-bright)',
        cursor: 'pointer',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-muted)',
        transition: 'border-color 0.15s, color 0.15s',
        fontSize: '15px',
      }}
      onMouseOver={(e) => {
        const el = e.currentTarget as HTMLButtonElement
        el.style.borderColor = 'var(--accent)'
        el.style.color = 'var(--accent)'
      }}
      onMouseOut={(e) => {
        const el = e.currentTarget as HTMLButtonElement
        el.style.borderColor = 'var(--rule-bright)'
        el.style.color = 'var(--text-muted)'
      }}
    >
      <span>{theme === 'dark' ? '☀' : '☾'}</span>
    </button>
  )
}

/* Form Component */
function AccessForm() {
  const [email, setEmail] = useState('')
  const [useCase, setUseCase] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !useCase) return

    // Log and redirect to register with pre-filled email
    console.log('Form submitted:', { email, useCase })
    window.location.href = `/register?email=${encodeURIComponent(email)}`
  }

  return (
    <div className="cta-panel">
      <div className="cta-panel-label">Join the beta</div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="form-field">
            <label>Email address</label>
            <input
              type="email"
              placeholder="you@domain.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-field">
            <label>Your use case</label>
            <select value={useCase} onChange={(e) => setUseCase(e.target.value)} required>
              <option value="" disabled>
                Select one
              </option>
              <option value="personal">Personal finance control</option>
              <option value="crypto">Global financial management</option>
              <option value="team">Team or business finances</option>
              <option value="dev">Building on top of BPal</option>
              <option value="other">Other</option>
            </select>
          </div>
          <button type="submit" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
            Create account →
          </button>
        </div>
      </form>

      <p className="form-note">No spam. No sales calls. We will onboard qualified users to the beta.</p>
    </div>
  )
}

/* Staggered Animation Helper */
function StaggerChild({ delay, children }: { delay: number; children: React.ReactNode }) {
  return (
    <div className="fade-up" style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  )
}

/* Main Landing Page */
export default function Home() {
  return (
    <>
      <FadeUpAnimations />

      {/* NAV */}
      {/* El padding va en la CLASE, no en el estilo en línea: un estilo en línea gana a la media
          query y por eso en el móvil la cabecera seguía con 60px a los lados (medido: el logo
          empezaba en x=60 en una pantalla de 390). */}
      <nav className="site-nav" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', borderBottom: '1px solid var(--rule)', background: 'var(--nav-bg)', backdropFilter: 'blur(12px)' }}>
        <a href="#" className="nav-logo">
          B<span style={{ color: 'var(--accent)' }}>.</span>Pal
        </a>
        <ul className="nav-links">
          <li><a href="#what">What it is</a></li>
          <li><a href="#capabilities">Capabilities</a></li>
          <li><a href="#different">Architecture</a></li>
          <li><a href="#trust">Security</a></li>
          {/* Estas dos SÍ se ven en el móvil (la clase lo dice): son la puerta de entrada. */}
          <li className="nav-cta-item"><a href="/login" className="nav-cta">Login</a></li>
          <li className="nav-cta-item"><a href="/register" className="nav-cta">Create account</a></li>
          <li><ThemeToggle /></li>
        </ul>
      </nav>

      {/* HERO */}
      <section id="hero">
        <div className="container">
          <div className="hero-grid">
            <div>
              <p className="hero-eyebrow fade-up">Financial infrastructure · Private beta</p>
              <h1 className="fade-up" style={{ transitionDelay: '80ms' }}>
                Your money.<br />
                <em>Under your control.</em>
              </h1>
              <p className="hero-sub fade-up" style={{ transitionDelay: '160ms' }}>
                BPal is not a banking app. It is a private financial platform — built on a real ledger, with programmable controls, card issuance, and risk systems that run beneath every transaction.
              </p>
              <div className="hero-actions fade-up" style={{ transitionDelay: '240ms' }}>
                <a href="/register" className="btn-primary">Create account →</a>
                <a href="#what" className="btn-secondary">See how it works</a>
              </div>

              <div className="ticker-bar fade-up" style={{ transitionDelay: '320ms' }}>
                {['Double-entry ledger', 'Secure financial accounts', 'Card issuance', 'Fraud detection', 'Programmable rules', 'Real-time balances'].map((item) => (
                  <div key={item} className="ticker-item">
                    <span className="dot"></span> {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="hero-stats fade-up" style={{ transitionDelay: '200ms' }}>
              <div className="stat-row">
                <div className="stat-num">100<span>%</span></div>
                <div className="stat-label">Ledger-backed balances</div>
              </div>
              <div className="stat-row">
                <div className="stat-num">&lt;<span>80</span>ms</div>
                <div className="stat-label">Transaction settlement</div>
              </div>
              <div className="stat-row">
                <div className="stat-num">24<span>/7</span></div>
                <div className="stat-label">Fraud monitoring</div>
              </div>
              <div className="stat-row">
                <div className="stat-num">0</div>
                <div className="stat-label">Hidden fees</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THIS IS */}
      <section id="what">
        <div className="container">
          <div className="two-col">
            <div className="fade-up">
              <div className="label">01 · What this is</div>
              <h2 className="section-title">
                Not a wallet.<br />
                Not a bank.<br />
                Infrastructure.
              </h2>
            </div>
            <div className="what-body">
              <p className="fade-up">Most financial apps are wrappers. They sit on top of someone else's infrastructure, enforce someone else's rules, and give you a view — not control.</p>
              <p className="fade-up">
                <strong>BPal is different at the stack level.</strong> Every balance is backed by a double-entry ledger. Every transaction is a ledger entry first, a payment second. The accounting is not an afterthought — it is the foundation.
              </p>
              <p className="fade-up">You get programmable controls. Spend limits that you set. Cards you can freeze, restrict, or scope to a single merchant category. Accounts that support multiple currencies. And a fraud system that flags anomalies before they become problems.</p>
              <div className="definition-block fade-up">
                <div className="def-term">In plain terms</div>
                <div className="def-body">If your current bank is a retail storefront, BPal is the financial OS running beneath it — except you have the keys.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section id="capabilities">
        <div className="container">
          <div className="cap-header">
            <div>
              <div className="label fade-up">02 · Core capabilities</div>
              <h2 className="section-title fade-up" style={{ transitionDelay: '80ms', marginTop: '16px' }}>
                Built for<br />
                real use.
              </h2>
            </div>
            <p className="fade-up" style={{ maxWidth: '320px', fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.7', paddingBottom: '4px', transitionDelay: '120ms' }}>
              Every feature below exists in the system today. Not on a roadmap. Not "coming soon."
            </p>
          </div>
          <div className="cap-grid">
            {[
              { num: 'CAP_01', name: 'Ledger-Backed Accounts', desc: 'Every account is a ledger account. Balances are always accurate. No reconciliation lag.', actions: ['Hold multiple currencies in one account', 'Real-time balance updates on every entry', 'Full audit trail on every credit and debit'] },
              { num: 'CAP_02', name: 'Internal Payments', desc: 'Move funds between accounts, users, or entities instantly. No rails, no delays, no fees for internal transfers.', actions: ['Instant settlement between BPal accounts', 'Batch payments with a single instruction', 'Programmable routing rules'], delay: '80ms' },
              { num: 'CAP_03', name: 'Card Issuance & Control', desc: 'Issue physical or virtual cards. Define exactly how each card can be used — by merchant, category, amount, or time.', actions: ['Freeze or unfreeze cards in real time', 'Set per-card spend limits and categories', 'Instant transaction notifications'], delay: '160ms' },
              { num: 'CAP_04', name: 'Deposits & Withdrawals', desc: 'Fund your account via bank transfer, crypto on-ramp, or direct deposit. Withdraw on your terms.', actions: ['ACH, SEPA, and SWIFT support', 'Crypto on-ramp and off-ramp', 'Withdrawal whitelists you control'] },
              { num: 'CAP_05', name: 'Fraud Detection', desc: 'A rule engine that monitors every transaction as it happens. You set the rules. The system enforces them.', actions: ['Velocity checks and pattern alerts', 'Geo-restriction by IP and card region', 'Automatic block + notification on breach'], delay: '80ms' },
              { num: 'CAP_06', name: 'Financial Controls', desc: 'Approval flows, role-based access, and spending policies. Built for individuals who want structure and teams who need it.', actions: ['Multi-signature approvals', 'Read-only observer roles', 'Spend policy enforcement at account level'], delay: '160ms' },
            ].map((cap) => (
              <div key={cap.num} className="cap-card fade-up" style={{ transitionDelay: cap.delay || '0ms' }}>
                <div className="cap-number">{cap.num}</div>
                <div className="cap-name">{cap.name}</div>
                <div className="cap-desc">{cap.desc}</div>
                <div className="cap-actions">
                  {cap.actions.map((action) => (
                    <div key={action} className="cap-action-item">{action}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section id="different">
        <div className="container">
          <div className="label fade-up">03 · Architecture</div>
          <h2 className="section-title fade-up" style={{ transitionDelay: '80ms', marginTop: '16px', maxWidth: '480px' }}>
            Built differently, at the level that matters.
          </h2>

          <div className="diff-grid">
            {[
              { label: 'Ledger-first', title: 'The ledger is the source of truth', body: 'Every transaction starts as a ledger entry. Balances are computed from entries — not stored as numbers that drift. This means zero reconciliation errors, a full audit trail for every account, and balances you can trust at any point in time.' },
              { label: 'Programmable controls', title: 'Rules that run before money moves', body: 'Every outgoing payment, card swipe, or withdrawal is evaluated against your control rules before it executes. Not after. This is the difference between a system that reports what happened and one that prevents what shouldn\'t.', delay: '80ms' },
              { label: 'Real-time risk', title: 'Fraud detection that doesn\'t sleep', body: 'The fraud engine monitors behavioral patterns, transaction velocity, and geographic signals around the clock. Anomalies are flagged in milliseconds. Actions — blocks, alerts, holds — happen automatically without waiting for a human review queue.' },
              { label: 'Unified stack', title: 'Multi-currency support in one ledger', body: 'Most systems treat different currencies as separate products bolted together. In BPal, all currencies live in the same accounting layer. A transfer from your USD account to your EUR account is a single ledger movement — not two systems talking to each other.', delay: '80ms' },
            ].map((diff, i) => (
              <div key={i} className="diff-item fade-up" style={{ transitionDelay: diff.delay || '0ms' }}>
                <div className="diff-label">{diff.label}</div>
                <div className="diff-title">{diff.title}</div>
                <div className="diff-body">{diff.body}</div>
              </div>
            ))}
          </div>

          {/* LEDGER VISUAL */}
          <div className="ledger-block fade-up" style={{ transitionDelay: '100ms' }}>
            <div className="ledger-header">
              <div className="ledger-title">General Ledger · Account #0x44AF · Live view</div>
              <div className="ledger-status">
                <span className="pulse"></span> SYNCED
              </div>
            </div>
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>Entry ID</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Credit</th>
                  <th style={{ textAlign: 'right' }}>Debit</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { id: 'LDG-0041', type: 'DEPOSIT', desc: 'ACH inbound · Chase ****1843', credit: '+5,000.00', debit: '—', balance: '12,840.55' },
                  { id: 'LDG-0042', type: 'CARD', desc: 'Stripe · Merchant #SG-904', credit: '—', debit: '−142.00', balance: '12,698.55' },
                  { id: 'LDG-0043', type: 'CONVERSION', desc: 'Currency conversion · 0.018 BTC equivalent', credit: '+1,103.22', debit: '—', balance: '13,801.77' },
                  { id: 'LDG-0044', type: 'TRANSFER', desc: 'Internal · Sub-account OP-02', credit: '—', debit: '−2,000.00', balance: '11,801.77' },
                  { id: 'LDG-0045', type: 'CARD', desc: 'AWS · Recurring charge', credit: '—', debit: '−89.00', balance: '11,712.77' },
                ].map((row) => (
                  <tr key={row.id}>
                    <td className="td-id">{row.id}</td>
                    <td className="td-type">{row.type}</td>
                    <td>{row.desc}</td>
                    <td className="td-credit">{row.credit}</td>
                    <td className="td-debit">{row.debit}</td>
                    <td className="td-balance">{row.balance}</td>
                    <td className="td-status-ok">SETTLED</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* TRUST / SECURITY */}
      <section id="trust">
        <div className="container">
          <div className="label fade-up">04 · Security</div>
          <h2 className="section-title fade-up" style={{ transitionDelay: '80ms', marginTop: '16px' }}>
            Safe. Not because we say so.
          </h2>

          <div className="trust-grid">
            {[
              { icon: '[ENC]', name: 'End-to-end encryption', body: 'All data in transit and at rest is encrypted with AES-256. Keys are scoped per account and rotated automatically.' },
              { icon: '[2FA]', name: 'Hardware-level 2FA', body: 'Every session requires a second factor. Passkeys, TOTP, and hardware security keys are all supported. SMS is not.', delay: '80ms' },
              { icon: '[LOG]', name: 'Immutable audit logs', body: 'Every API call, login, card swipe, and ledger entry is logged and tamper-proof. You can audit everything. So can we.', delay: '160ms' },
              { icon: '[ISO]', name: 'Enhanced security option', body: 'For certain accounts, you can opt for enhanced security mode. Your access remains fully protected. We maintain secure infrastructure safeguards.', delay: '240ms' },
            ].map((trust, i) => (
              <div key={i} className="trust-item fade-up" style={{ transitionDelay: trust.delay || '0ms' }}>
                <div className="trust-icon">{trust.icon}</div>
                <div className="trust-name">{trust.name}</div>
                <div className="trust-body">{trust.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="cta">
        <div className="container">
          <div className="cta-inner">
            <div className="fade-up">
              <div className="label" style={{ marginBottom: '28px' }}>05 · Beta Access</div>
              <h2 className="cta-heading">
                Ready to take control?<br />
                <em>Create your account now.</em>
              </h2>
              <p className="cta-sub">
                BPal is in private beta. We are onboarding users who want real financial infrastructure — not another app with a pastel card and a round-up feature.
              </p>
              <p className="cta-sub" style={{ marginTop: '12px' }}>
                If that sounds like you, join the beta. You can start using BPal immediately after creating your account.
              </p>
            </div>

            <div className="fade-up" style={{ transitionDelay: '120ms' }}>
              <AccessForm />
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div className="footer-brand">
            B<span>.</span>Pal — Financial Infrastructure
          </div>
          <ul className="footer-links">
            <li><a href="/login">Login</a></li>
            <li><a href="/privacy">Privacy</a></li>
            <li><a href="/terms">Terms</a></li>
            <li><a href="mailto:partners@bpal.io">Contact</a></li>
          </ul>
        </div>
      </footer>
    </>
  )
}
